import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";

import { AccordionModel, AccordionPanel, AccordionPanelHeadingTag } from "@courses-pm2/api/src/ts/models";
import { Engine } from "@courses-pm2/api/src/ts/utils";

import { getChildrenByTagName } from "@courses-pm2/api/src/ts/parser/getChildrenByTagName";
import { parseChildElements } from "@courses-pm2/api/src/ts/parser/parseChildElements";

/**
 * Helper that parses <Accordion /> elements in the response from the pm2
 * backend.
 */
export async function parseAccordion({ node, portalId, user, engine }: {
    node: Element;
    portalId: string;
    user?: AuthenticatedUserModel;
    engine?: Engine;
}): Promise<AccordionModel> {
    // Announce that a model has been identified if possible
    if (engine) {
        engine.announce(AccordionModel);
    }

    // Iterate over the accordion panels, and begin parsing the elements
    // within them
    const panels: AccordionPanel[] = [];

    for (const panel of getChildrenByTagName(node, "AccordionChild")) {
        // Extract the actual title of the panel
        const title = decodeURIComponent(panel.getAttribute("title") || "");

        // Determine the heading tag to use when rendering the title of
        // the panel
        let headingTag = <AccordionPanelHeadingTag>(panel.getAttribute("headingTag") || "").toLowerCase();

        switch (headingTag) {
            case "h1":
            case "h2":
            case "h3":
            case "h4":
            case "h5":
            case "h6":
                break;

            default:
                headingTag = "h4";
        }

        // Push to the list of panels
        panels.push({
            content: await parseChildElements({
                node: panel,
                portalId,
                user,
                engine,
            }),
            expandInitially: panel.getAttribute("open-initially") === "true",
            headingTag,
            title,
        });
    }

    return new AccordionModel({
        isScrambled: parseInt(node.getAttribute("access") ?? "0", 10) > 0,
        autoCollapse: node.getAttribute("auto-collapse") === "true",
        panels,
        portalId,
        engine,
    });
}
